import { useState, useEffect, useCallback } from 'react';
import { Facebook, Twitter, Linkedin, Copy, ExternalLink, RefreshCw, Check, Settings, Key } from 'lucide-react';
import api from '../utils/api';

export default function SocialTab({ headers }) {
  const [posts, setPosts] = useState([]);
  const [fbConfig, setFbConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [showFbSetup, setShowFbSetup] = useState(false);
  const [fbPageId, setFbPageId] = useState('');
  const [fbToken, setFbToken] = useState('');
  const [generating, setGenerating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [p, fc] = await Promise.all([
        api.get('/admin/social/posts', { headers }),
        api.get('/admin/social/facebook-config', { headers }),
      ]);
      setPosts(p.data);
      setFbConfig(fc.data);
    } catch (e) {
      console.error('Social fetch error:', e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generateAll = async () => {
    setGenerating(true);
    try {
      await api.post('/admin/social/generate-all', {}, { headers });
      fetchData();
    } catch (e) { console.error(e); }
    finally { setGenerating(false); }
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const publishToFacebook = async (postId) => {
    try {
      const res = await api.post(`/admin/social/publish/facebook/${postId}`, {}, { headers });
      if (res.data.published) {
        fetchData();
      }
    } catch (e) { console.error(e); }
  };

  const saveFbConfig = async () => {
    try {
      await api.put('/admin/social/facebook-config', { page_id: fbPageId, access_token: fbToken }, { headers });
      setShowFbSetup(false);
      setFbPageId('');
      setFbToken('');
      fetchData();
    } catch (e) { console.error(e); }
  };

  if (loading) return <p className="text-muted-foreground text-sm">Loading social posts...</p>;

  return (
    <div className="space-y-6" data-testid="social-tab">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <p className="text-xs text-muted-foreground">{posts.length} social posts generated</p>
        </div>
        <div className="flex gap-2">
          <button onClick={generateAll} disabled={generating}
            className="flex items-center gap-1 px-3 py-2 text-xs bg-primary text-primary-foreground rounded-sm font-semibold hover:bg-white hover:text-black transition-colors disabled:opacity-50"
            data-testid="generate-all-social">
            <RefreshCw className={`w-3 h-3 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Generating...' : 'Generate All'}
          </button>
          <button onClick={() => setShowFbSetup(!showFbSetup)}
            className="flex items-center gap-1 px-3 py-2 text-xs border border-white/20 rounded-sm text-foreground hover:border-primary hover:text-primary"
            data-testid="fb-setup-toggle">
            <Settings className="w-3 h-3" /> FB Setup
          </button>
        </div>
      </div>

      {/* Facebook Setup */}
      {showFbSetup && (
        <div className="bg-white/5 border border-white/10 rounded-sm p-5" data-testid="fb-setup-form">
          <h3 className="font-serif text-lg mb-3 flex items-center gap-2"><Key className="w-4 h-4 text-primary" /> Facebook Auto-Post Setup</h3>
          {fbConfig?.configured ? (
            <p className="text-xs text-green-400 mb-3">Facebook is configured (token: {fbConfig.access_token})</p>
          ) : (
            <div className="text-xs text-muted-foreground mb-4 space-y-2">
              <p className="font-semibold text-foreground">How to get a Facebook Page Access Token:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">developers.facebook.com</a> and create an app</li>
                <li>Add the <strong>Pages</strong> product to your app</li>
                <li>Go to <strong>Tools &gt; Graph API Explorer</strong></li>
                <li>Select your app, then request <code className="bg-white/10 px-1">pages_manage_posts</code> + <code className="bg-white/10 px-1">pages_read_engagement</code> permissions</li>
                <li>Generate a <strong>Page Access Token</strong> (not User token)</li>
                <li>For a long-lived token, exchange it via the <a href="https://developers.facebook.com/docs/pages-api/overview" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Pages API docs</a></li>
                <li>Paste your Page ID and Token below</li>
              </ol>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <input value={fbPageId} onChange={e => setFbPageId(e.target.value)} placeholder="Facebook Page ID"
              className="bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm flex-1" data-testid="fb-page-id" />
            <input value={fbToken} onChange={e => setFbToken(e.target.value)} placeholder="Page Access Token" type="password"
              className="bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm flex-1" data-testid="fb-access-token" />
            <button onClick={saveFbConfig} className="bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold rounded-sm" data-testid="fb-save-config">
              Save
            </button>
          </div>
        </div>
      )}

      {/* Social Posts */}
      {posts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Facebook className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No social posts yet. Click "Generate All" to create social copy for all blog posts.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map(post => (
            <div key={post.id} className="bg-white/5 border border-white/10 rounded-sm p-5" data-testid={`social-post-${post.id}`}>
              <p className="font-serif text-sm mb-3">{post.blog_title}</p>

              {/* Platform copies */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Facebook */}
                <div className="border border-white/5 rounded-sm p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1 text-blue-500"><Facebook className="w-3.5 h-3.5" /> <span className="text-xs font-semibold">Facebook</span></div>
                    <div className="flex gap-1">
                      <button onClick={() => copyToClipboard(post.facebook.copy, `fb-${post.id}`)}
                        className="text-muted-foreground hover:text-foreground" data-testid={`copy-fb-${post.id}`}>
                        {copied === `fb-${post.id}` ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                      </button>
                      <a href={post.facebook.share_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground line-clamp-4 whitespace-pre-line">{post.facebook.copy}</p>
                  {fbConfig?.configured && !post.facebook.published && (
                    <button onClick={() => publishToFacebook(post.id)}
                      className="mt-2 text-[10px] text-blue-400 hover:underline" data-testid={`publish-fb-${post.id}`}>
                      Auto-Post to Facebook
                    </button>
                  )}
                  {post.facebook.published && (
                    <p className="mt-2 text-[10px] text-green-400">Published</p>
                  )}
                </div>

                {/* Twitter/X */}
                <div className="border border-white/5 rounded-sm p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1"><Twitter className="w-3.5 h-3.5" /> <span className="text-xs font-semibold">X / Twitter</span></div>
                    <div className="flex gap-1">
                      <button onClick={() => copyToClipboard(post.twitter.copy, `tw-${post.id}`)}
                        className="text-muted-foreground hover:text-foreground" data-testid={`copy-tw-${post.id}`}>
                        {copied === `tw-${post.id}` ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                      </button>
                      <a href={post.twitter.share_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground line-clamp-4 whitespace-pre-line">{post.twitter.copy}</p>
                </div>

                {/* LinkedIn */}
                <div className="border border-white/5 rounded-sm p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1 text-blue-400"><Linkedin className="w-3.5 h-3.5" /> <span className="text-xs font-semibold">LinkedIn</span></div>
                    <div className="flex gap-1">
                      <button onClick={() => copyToClipboard(post.linkedin.copy, `li-${post.id}`)}
                        className="text-muted-foreground hover:text-foreground" data-testid={`copy-li-${post.id}`}>
                        {copied === `li-${post.id}` ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                      </button>
                      <a href={post.linkedin.share_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground line-clamp-4 whitespace-pre-line">{post.linkedin.copy}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
